import type { AttentionDecision, AttentionEvent, AttentionHistoryEntry } from './velis-attention'
import type { MemoryDecision, MemoryRecord } from './velis-memory'
import type { HeldContextEntry } from './velis-perception'
import type { Reminder } from './velis-reminder'

import { decideAttention } from './velis-attention'
import { decideMemory } from './velis-memory'
import { applyReminder } from './velis-reminder'

export interface CompanionRun {
  recall: string
  context: string
  attention: AttentionDecision
  history: AttentionHistoryEntry[]
  reminders: Reminder[]
  tool: string
  memory: MemoryDecision
}

function recallProject(records: readonly MemoryRecord[]) {
  const active = records.find(record => record.status === 'active' && /velis|项目|project/i.test(record.content))
  return active?.content ?? 'none'
}

function skipMemory(sessionId: string, reason: string): MemoryDecision {
  return { trace: { action: 'SKIP', reason, source: 'user', sessionId } }
}

/**
 * Runs one project-companion pass: recall, held context, attention, one reminder, then memory.
 * A suppressed event does not write the reminder or the memory again.
 *
 * @example
 * runProjectCompanion({ now: 1_000, records: [], held: [], event, history: [], reminders: [], confirmed: false, sessionId: 's', createId: () => 'id' })
 */
export function runProjectCompanion(input: {
  now: number
  records: readonly MemoryRecord[]
  held: readonly HeldContextEntry[]
  event: AttentionEvent
  history: readonly AttentionHistoryEntry[]
  reminders: readonly Reminder[]
  confirmed: boolean
  sessionId: string
  createId: () => string
}): CompanionRun {
  const recall = recallProject(input.records)
  const context = input.held.map(entry => entry.summary).join(' ').trim() || 'none'
  const attention = decideAttention({
    event: input.event,
    now: input.now,
    history: input.history,
    contexts: input.held.map(entry => ({ slot: entry.slot, summary: entry.summary })),
  })
  const base = {
    recall,
    context,
    attention: attention.decision,
    history: attention.history,
  }

  if (attention.decision.suppressed || attention.decision.final_action === 'IGNORE') {
    return {
      ...base,
      reminders: [...input.reminders],
      tool: 'skipped',
      memory: skipMemory(input.sessionId, 'attention-suppressed'),
    }
  }

  if (!input.confirmed) {
    return {
      ...base,
      reminders: [...input.reminders],
      tool: 'requires_confirmation',
      memory: skipMemory(input.sessionId, 'needs-confirmation'),
    }
  }

  const text = input.event.summary?.trim() || 'Check the Velis project'
  const saved = applyReminder(input.reminders, {
    action: 'create',
    id: input.event.dedupe_key,
    text,
    dueAt: new Date(input.now + 60 * 60 * 1000).toISOString(),
  })
  if (saved.status !== 'saved' || !saved.reminder) {
    return {
      ...base,
      reminders: saved.reminders,
      tool: saved.status,
      memory: skipMemory(input.sessionId, 'reminder-rejected'),
    }
  }

  return {
    ...base,
    reminders: saved.reminders,
    tool: `saved ${saved.reminder.id}`,
    memory: decideMemory({
      createId: input.createId,
      message: `Current task: ${saved.reminder.text}`,
      now: new Date(input.now).toISOString(),
      records: [...input.records],
      sessionId: input.sessionId,
    }),
  }
}

/**
 * Renders one companion pass as a single trace line.
 *
 * @example
 * formatCompanionTrace(run)
 * // => 'event-1 · recall Velis · context none · NOTIFY · saved reminder:check · memory WRITE · clear'
 */
export function formatCompanionTrace(run: CompanionRun) {
  const flags = [
    run.attention.deduped ? 'deduped' : undefined,
    run.attention.suppressed ? 'suppressed' : undefined,
  ].filter((flag): flag is string => Boolean(flag))
  return [
    run.attention.event.event_id,
    `recall ${run.recall}`,
    `context ${run.context}`,
    run.attention.final_action,
    run.tool,
    `memory ${run.memory.trace.action}`,
    flags.length > 0 ? flags.join(', ') : 'clear',
  ].join(' · ')
}
